import { z } from "zod";

/** Credit packs and auto-reload (M7 round five; brief `docs/briefs/m7-billing.md` task 4, draft 2's B4, the founder's answers 7–9): the
 *  packs on sale, the owner's auto-reload choices, and the pure rules of when a reload is wanted, when our limits stop it and what a
 *  refund takes back. The webhook, the reload job and the screens read these; nothing here talks to Stripe or the database. */

/** a pack's id travels in addresses and Stripe's metadata: lower-case letters, digits, `-` and `_` */
export const PackIdSchema = z.string().regex(/^[a-z0-9_-]{1,40}$/);
/** the three packs the product starts with (the prototype's; migration 0056 inserts the same rows) */
export const DEFAULT_PACKS = [
  { id: "pack50", credits: 50, priceCents: 1_500, active: true, sort: 1 },
  { id: "pack220", credits: 220, priceCents: 6_000, active: true, sort: 2 },
  { id: "pack600", credits: 600, priceCents: 15_000, active: true, sort: 3 },
] as const;

/** the back office's pack row (`credit_packs`): its Stripe price is the setup script's, null until this environment has one */
export const CreditPackSchema = z.object({
  id: PackIdSchema, credits: z.number().int().min(1), priceCents: z.number().int().min(1), active: z.boolean(), sort: z.number().int().min(0),
  stripePriceId: z.string().nullable(),
}).strict();
export type CreditPack = z.infer<typeof CreditPackSchema>;
/** the body of `PUT /api/admin/packs/:id`: no id (the address names it), no Stripe price (the setup script writes it) */
export const AdminPackSchema = z.object({ credits: z.number().int().min(1), priceCents: z.number().int().min(1), active: z.boolean(), sort: z.number().int().min(0) }).strict();
export type AdminPack = z.infer<typeof AdminPackSchema>;
export const AdminPacksReadSchema = z.object({ rows: z.array(CreditPackSchema) });
/** `GET /api/v1/w/:slug/packs`: the packs on sale, in their order */
export const PacksReadSchema = z.object({ rows: z.array(z.object({ id: z.string(), credits: z.number().int(), priceCents: z.number().int() })) });
export type PacksRead = z.infer<typeof PacksReadSchema>;

/** The owner's auto-reload (B4): off until switched on; below `below` credits it buys `packId`, at most `monthlyMax` packs a billing
 *  period. Stored whole in `organization.settings.autoReload`: the settings write merges one level deep, so a change always carries all
 *  four choices (a partial object would reset the others). */
export const AutoReloadSchema = z.object({ on: z.boolean(), below: z.number().int().min(1), packId: PackIdSchema, monthlyMax: z.number().int().min(1) }).strict();
export type AutoReload = z.infer<typeof AutoReloadSchema>;
export const AUTO_RELOAD_DEFAULT: AutoReload = { on: false, below: 10, packId: "pack220", monthlyMax: 3 };

/** our limit per workspace and UTC day (B4), and the tries of one reload's invoice (one, plus "at most twice" more) */
export const RELOAD_DAILY_LIMIT = 2;
export const RELOAD_MAX_TRIES = 3;
/** a reload's record (`credit_reloads.state`); the first four are "in flight" (one per workspace) */
export const RELOAD_STATES = ["queued", "open", "declined", "action_required", "paid", "failed", "skipped"] as const;
export type ReloadState = (typeof RELOAD_STATES)[number];
export const RELOAD_IN_FLIGHT: readonly ReloadState[] = ["queued", "open", "declined", "action_required"];

/** a charge that leaves the balance here wants the workspace's reload (the job decides the limits) */
export const reloadWanted = (a: AutoReload, balance: number): boolean => a.on && balance < a.below;
/** what stops a reload: the owner's own monthly maximum first, then our daily limit; null when nothing does */
export function reloadLimit(o: { today: number; thisPeriod: number; monthlyMax: number }): "monthly_max" | "daily_limit" | null {
  if (o.thisPeriod >= o.monthlyMax) return "monthly_max";
  if (o.today >= RELOAD_DAILY_LIMIT) return "daily_limit";
  return null;
}
/** The credits a refund takes back now: the pack's credits times the refunded share of what was paid (Stripe's cumulative
 *  `amount_refunded`), rounded down so never more than was refunded, minus what earlier refunds of the same payment took back; never
 *  below zero (a repeated notice takes nothing). */
export function refundReversal(o: { credits: number; amountCents: number; refundedCents: number; reversed: number }): number {
  if (o.amountCents <= 0) return 0;
  const share = Math.min(o.refundedCents, o.amountCents) / o.amountCents;
  return Math.max(0, Math.floor(o.credits * share + 1e-9) - o.reversed);
}

/** `GET /credits`' auto-reload line: paused (the bank asked the customer to confirm, or three declines) until the owner switches it on
 *  again; the reload packs bought this period (against the monthly maximum) */
export const AutoReloadReadSchema = z.object({ paused: z.boolean(), reason: z.enum(["action_required", "declined"]).nullable(), thisPeriod: z.number().int() });
export type AutoReloadRead = z.infer<typeof AutoReloadReadSchema>;
